import React, { useRef, useState, useEffect } from 'react';
import {
  Button, Typography, Grid, Accordion, AccordionSummary, AccordionDetails,
  Box, TextField, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { VendorSelectionProps } from './types';

export const VendorSelection: React.FC<VendorSelectionProps> = ({
  scheduleType,
  vendors = [],
  vendorMembers = [],
  currentMembers = [],
  customers = [],
  customerId,
  onVendorMemberSelect,
  onCustomerSelect,
  expandedVendors = [],
  onVendorExpand
}) => {
  // スクロール用のコンテナ参照
  const containerRef = useRef<HTMLDivElement>(null);
  
  // 検索キーワード状態
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  
  // スクロール位置をリセット
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    // スケジュールタイプが変わったら、検索キーワードをリセット
    setSearchKeyword('');
  }, [scheduleType]);

  // 検索キーワードに基づいてフィルタリングされた顧客リスト
  const filteredCustomers = customers.filter(customer => 
    customer.Customer_Name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 工事予定の場合は外注メンバー選択を表示
  if (scheduleType === 'construction') {
    const selectedVendorMemberIds = currentMembers
      .filter(m => m.VendorMember_ID)
      .map(m => m.VendorMember_ID as number);

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden'
      }}>
        {/* スクロール要素 */}
        <div 
          ref={containerRef}
          style={{
            flex: '1 1 auto',
            overflowY: 'auto',
            width: '100%',
            height: 'calc(100% - 40px)',
            paddingTop: '8px'
          }}
        >
          {/* 外注業者アコーディオン */}
          {vendors.map((vendor) => (
            <Accordion
              key={vendor.ID}
              expanded={expandedVendors.includes(vendor.ID)}
              onChange={(_, isExpanded) => {
                if (onVendorExpand) {
                  onVendorExpand(vendor.ID, isExpanded);
                }
              }}
              sx={{ 
                mb: 1,
                '&:first-of-type': {
                  mt: 0
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 'medium' }}>{vendor.VendorName}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1}>
                  {vendorMembers
                    .filter(m => m.VendorID === vendor.ID)
                    .map(member => {
                      const isSelected = selectedVendorMemberIds.includes(member.ID);

                      return (
                        <Grid item xs={6} key={`vendor-${member.ID}`}>
                          <Button
                            fullWidth
                            variant={isSelected ? "contained" : "outlined"}
                            color={isSelected ? "success" : "primary"}
                            onClick={() => {
                              if (onVendorMemberSelect) {
                                onVendorMemberSelect(member, vendor.VendorName);
                              }
                            }}
                            sx={{
                              height: '50px',
                              fontSize: '0.75rem',
                              whiteSpace: 'normal',
                              lineHeight: 1.2,
                              ...(isSelected && {
                                fontWeight: 'bold'
                              })
                            }}
                          >
                            {member.MemberName}
                          </Button>
                        </Grid>
                      );
                    })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
        
        {/* スクロールヒント */}
        <Typography 
          variant="caption" 
          color="text.secondary"
          align="center"
          sx={{ 
            mt: 1,
            mb: 1,
            flexShrink: 0 
          }}
        >
          スクロールしてさらに表示
        </Typography>
      </Box>
    );
  }

  // その他予定の場合は顧客一覧を表示
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Typography variant="subtitle2" sx={{ mb: 1, flexShrink: 0 }}>
        顧客を選択（任意）
      </Typography>
      
      {/* 顧客検索フィールド */}
      <TextField
        fullWidth
        size="small"
        placeholder="顧客名を検索..."
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        sx={{ mb: 1, flexShrink: 0 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <div
        ref={containerRef}
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '8px',
          width: '100%',
          paddingTop: '8px'
        }}
      >
        {filteredCustomers.length > 0 ? (
          filteredCustomers.map(customer => (
            <Button
              key={customer.ID}
              variant={customerId === customer.ID ? "contained" : "outlined"}
              size="small"
              onClick={() => {
                if (onCustomerSelect) {
                  onCustomerSelect(customer.ID, customer.Customer_Name);
                }
              }}
              style={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                textAlign: 'left',
                padding: '8px',
                minHeight: '44px'
              }}
            >
              {customer.Customer_Name}
            </Button>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 2 }}>
            検索条件に一致する顧客がありません
          </Typography>
        )}
      </div>

      {filteredCustomers.length > 0 && (
        <Typography 
          variant="caption" 
          color="text.secondary"
          align="center"
          sx={{ mt: 1, flexShrink: 0 }}
        >
          スクロールしてさらに表示
        </Typography>
      )}
    </Box>
  );
};